import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCalendarZmanim } from '../hooks/useCalendarZmanim';
import { isGoldDay } from '../utils/minyanDay';
import DayLabels from '../components/common/DayLabels';
import { getTodayString } from '../utils/dateShortcuts';
import HebrewDatePicker from '../components/common/HebrewDatePicker';
import { ZMAN_DISPLAY_FIELDS } from '../utils/zmanimDisplay';


function shiftDate(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function CalendarPage() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('seo.calendar'), { description: t('seo.desc.calendar') });
  const isHebrew = i18n.language === 'he';

  const [selectedDate, setSelectedDate] = useState(getTodayString);
  const { zmanim, source, loading, error } = useCalendarZmanim(selectedDate);

  const isGoldTheme = isGoldDay(selectedDate);

  const formattedDate = new Intl.DateTimeFormat(isHebrew ? 'he' : 'en', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${selectedDate}T12:00:00`));

  const sourceLabel =
    source === 'myzmanim'
      ? t('calendar.sourceMyZmanim')
      : source === 'hebcal'
        ? t('calendar.sourceHebcal')
        : null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <header className="space-y-3">
        <h1 className="text-2xl font-bold text-ink">{t('calendar.heading')}</h1>
        <p className="text-sm text-ash">{formattedDate}</p>

        {/* LTR keeps prev← →next predictable in both languages */}
        <div dir="ltr" className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedDate((d) => shiftDate(d, -1))}
            className="shrink-0 p-2 rounded-[10px] border border-ink/15 text-ink hover:bg-fog transition-colors"
            aria-label={t('calendar.previousDay')}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <HebrewDatePicker selectedDate={selectedDate} onChange={setSelectedDate} />
          <button
            type="button"
            onClick={() => setSelectedDate((d) => shiftDate(d, 1))}
            className="shrink-0 p-2 rounded-[10px] border border-ink/15 text-ink hover:bg-fog transition-colors"
            aria-label={t('calendar.nextDay')}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <DayLabels date={selectedDate} />
      </header>

      {loading && (
        <div className="bg-paper border border-ink/10 rounded-[16px] p-4 space-y-2">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 rounded-[10px] bg-fog animate-pulse" />
          ))}
        </div>
      )}

      {!loading && error && (
        <p className="text-center text-sm text-danger-500 py-8">{t('common.error')}</p>
      )}

      {!loading && !error && zmanim && (
        <div className="space-y-3">
          {zmanim.candleLighting && (
            <div
              className={`flex items-center justify-between gap-3 rounded-[16px] border px-4 py-3 ${
                isGoldTheme
                  ? 'border-gold-700/20 bg-gold-50'
                  : 'border-ink/10 bg-fog'
              }`}
            >
              <span className="text-sm font-medium text-ink">
                {t('zmanim.candleLighting')}
              </span>
              <span className="text-base font-bold tabular-nums text-ink">
                {zmanim.candleLighting}
              </span>
            </div>
          )}

          <div className="bg-paper border border-ink/10 rounded-[16px] p-4">
            <ul className="divide-y divide-ink/10">
              {ZMAN_DISPLAY_FIELDS.map(({ field, i18nKey }) => (
                <li
                  key={field}
                  className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <span className="text-sm text-ink">{t(`zmanim.${i18nKey}`)}</span>
                  <span className="text-sm font-semibold tabular-nums text-ink shrink-0">
                    {zmanim[field] as string}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {sourceLabel && (
            <p className="text-xs text-ash text-center">
              {t('calendar.sourceCaption', {
                location: t('calendar.location'),
                source: sourceLabel,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
